import { ConfigurationService, HistoryService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { isApplicationModuleGroup } from '../application-module-registry.js';
import { getAvailableModules } from './available-modules.js';
const AT_SIGN = '@';
/**
 * This method receives a array of routes and flattens it to a single resulting array, containing all nested routes.
 *
 * @param routes - the array to be flatten.
 */
function flattenRoutes(routes) {
    const result = [];
    for (const route of routes) {
        result.push(route, ...flattenRoutes(route.children));
    }
    return result;
}
/**
 * Given a array of routes, return the first route that has the `main: true` value, and the first array value, if a
 * `main` anotated route is not found.
 *
 * @param routes - the routes array.
 */
function getMainRoute(routes) {
    const mainChildren = routes.find((child) => child.main === true);
    return mainChildren ?? routes[0];
}
/**
 * Get all the siblings routes of a given route.
 *
 * @param actual - the route to be compared against.
 * @param routes - the routes array to be checked.
 */
function getSiblings(actual, routes) {
    const siblings = new Set();
    for (const { id, name } of routes) {
        if (actual.name === name) {
            continue;
        }
        siblings.add(id);
    }
    return siblings;
}
/**
 * Guess the active application module based on the current route of the application.
 *
 * **This method is intend to the first load of the application, trying to access a module route directly.**
 */
function guessModuleBasedOnRoute() {
    const modules = getAvailableModules();
    const route = HistoryService.getCurrentRoute()
        .replace(ConfigurationService.getBaseURL(), '');
    return findModule(Array.from(modules.values()), route);
}
function findModule(modules, route) {
    let bestMatch = null;
    for (const module of modules) {
        if (isApplicationModuleGroup(module)) {
            const moduleFound = findModule(module.modules, route);
            if (moduleFound) {
                return moduleFound;
            }
            continue;
        }
        if (route === module.route) {
            return module;
        }
        if (route.startsWith(module.route) && !bestMatch) {
            bestMatch = module;
        }
    }
    return bestMatch;
}
/**
 * This method is responsible for recursively mapping a array of routes, creating a single id for each of the items.
 *
 * @param parentId - the parent id
 * @param parentRoute - the parent route
 * @param routes - the routes array
 */
function mapRoutes(parentId, parentRoute, routes = []) {
    const mappedRoutes = routes.map((routeObject) => {
        const { children, name, route } = routeObject;
        const completeRoute = route.startsWith(parentRoute)
            ? route
            : `${parentRoute}${route}`;
        const id = `${parentId}${AT_SIGN}${name}`;
        return {
            ...routeObject,
            children: mapRoutes(id, completeRoute, children),
            completeRoute,
            id,
            route,
        };
    });
    return mappedRoutes.map((routeObject) => ({
        ...routeObject,
        siblings: getSiblings(routeObject, mappedRoutes),
    }));
}
/**
 * This method converts an array of routes to an map of routes, using the `id` property as a key.
 *
 * @param routes - the array to be converted
 */
function routeArrayToMap(routes) {
    const map = new Map();
    for (const route of routes) {
        map.set(route.id, route);
    }
    return map;
}
/**
 * This method retuns the route level.
 *
 * @param id - The route id
 */
function getRouteLevel(id) {
    return id.split(AT_SIGN).length;
}
export { flattenRoutes, getMainRoute, getRouteLevel, guessModuleBasedOnRoute, mapRoutes, routeArrayToMap, };
//# sourceMappingURL=application-routes.js.map